// /sign-in#token=...&door=...: the login link's page (CLAUDE.md, Security: "a link that lands on a page
// with a Log in button" ("Log in" everywhere, Adam, 11 Oct), "because work email scanners open links
// and use them up"; decision 41). The login code email carries the link beside the code (request-code),
// with the same login's token after the #, which no server sees. The address stays /sign-in: it's an
// address, not words on the page, and it's what request-code writes.
// - Opening the page does nothing with the token: no call at all until Log in is pressed, so a scanner
//   that opens the link, even 1 that runs the page, uses nothing up.
// - Log in verifies the token (lib/auth.js verifyLink), and App takes them to the Library, as the
//   code login does (decision 29), with the token gone from the address.
// - It wears the look of the door the code was asked from, when the link names 1, else the Library's.
// - A link that's been used, has run out or has no token says so, on its door, with "Email me a new
//   code" (Login with linkFailed); the token is taken out of the address. A failure worth trying again
//   (no answer, Supabase's side) keeps the button, with the login's line for it.
// - Logged in already, App opens the Library and never uses the token (it runs out on its own).
// - A newer link opened in a tab already on this page changes only the part after the #, so the page
//   reads the link again then, as it does when it opens.
// No board draws this page: it's the board's door with 1 button, and its words are in the board's tone:
// its heading is the door's own ("Log in to <product>", or "Log in" on the Library's), and so is its button.
import { useEffect, useState } from 'react'
import { setTitle } from '../config/site.js'
import { DOORS } from '../config/doors.js'
import { verifyLink, recordLogin } from '../lib/auth.js'
import { readLoginLink, linkIsGone, SIGN_IN_PATH } from '../lib/loginLink.js'
import Login, { DoorFrame } from './Login.jsx'
import '../styles/login.css'

export const SIGN_IN_WORDS = {
  lede: 'Press Log in to open your Library.',
  button: 'Log in',
  retry: "That didn't go through. Check your connection and try again.",
}

export default function SignIn() {
  // read as the page opens, and again if another link is opened in this tab; nothing is sent
  const [link, setLink] = useState(() => readLoginLink(window.location.hash))
  const door = DOORS[link.door] || DOORS.library
  const [gone, setGone] = useState(!link.token)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { if (!gone) setTitle('Log in') }, [gone])

  useEffect(() => {
    function reread() {
      const next = readLoginLink(window.location.hash)
      setLink(next)
      setGone(!next.token)
      setBusy(false)
      setError('')
    }
    window.addEventListener('hashchange', reread)
    return () => window.removeEventListener('hashchange', reread)
  }, [])

  async function signIn(e) {
    e.preventDefault()
    if (busy || !link.token) return
    setError('')
    setBusy(true)
    let result
    try {
      result = await verifyLink(link.token)
    } catch (err) {
      result = { error: err }
    }
    if (!result?.error) {
      // the link is the email code's own token, so it's recorded as a code login (login_events
      // takes code or password); App moves them on to the Library as the session starts
      recordLogin('code')
      return
    }
    setBusy(false)
    if (linkIsGone(result.error)) {
      // spent: out of the address, keeping only the door
      const keep = door.key === 'library' ? SIGN_IN_PATH : `${SIGN_IN_PATH}#door=${door.key}`
      window.history.replaceState(window.history.state, '', keep)
      setGone(true)
    } else {
      setError(SIGN_IN_WORDS.retry)
    }
  }

  if (gone) return <Login key={door.key} door={door} linkFailed />
  return (
    <DoorFrame door={door}>
      <form className="door-form" onSubmit={signIn} noValidate>
        <h1 className="door-title">{door.name ? `Log in to ${door.name}` : 'Log in'}</h1>
        <p className="door-lede">{SIGN_IN_WORDS.lede}</p>
        <button type="submit" className="door-btn" disabled={busy}>{SIGN_IN_WORDS.button}</button>
        {error && <p className="door-error" role="alert">{error}</p>}
      </form>
    </DoorFrame>
  )
}
